import React, { useCallback, useRef, useState } from 'react';
import { Upload, Camera, X, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const MAX_FILE_SIZE = 8 * 1024 * 1024;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PhotoUploader({ value = [], onChange, max = 8, testid = 'photo-uploader' }) {
  const inputRef = useRef(null);
  const camRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const addFiles = useCallback(async (files) => {
    const list = Array.from(files);
    const next = [...value];
    for (const f of list) {
      if (next.length >= max) break;
      if (!/^image\/(jpeg|jpg|png|webp|heic|heif)$/.test(f.type)) {
        toast.error(`Nicht unterstützter Dateityp: ${f.name}`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`Datei zu groß (max 8 MB): ${f.name}`);
        continue;
      }
      try {
        const dataUrl = await readAsDataUrl(f);
        next.push({
          filename: f.name,
          mime_type: f.type,
          dataUrl,
          size: f.size,
        });
      } catch (e) {
        toast.error(`Fehler beim Lesen: ${f.name}`);
      }
    }
    onChange?.(next);
  }, [value, onChange, max]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };

  const removeAt = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange?.(next);
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`rounded-2xl border-2 border-dashed p-5 sm:p-6 text-center transition-colors ${dragging ? 'border-[hsl(var(--accent))] bg-blue-50' : 'border-slate-200 bg-slate-50'}`}
      >
        <ImageIcon className="h-8 w-8 mx-auto text-slate-400" />
        <p className="mt-2 text-sm text-slate-700">Fotos hier ablegen oder auswählen</p>
        <p className="text-xs text-slate-500">JPG, PNG, WEBP • max. 8 MB pro Foto • bis zu {max} Fotos</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button type="button" onClick={() => camRef.current?.click()} className="bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" data-testid="wizard-photo-capture-button">
            <Camera className="h-4 w-4 mr-1.5" /> Foto aufnehmen
          </Button>
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} data-testid="wizard-photo-upload-input">
            <Upload className="h-4 w-4 mr-1.5" /> Datei wählen
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
          data-testid="wizard-photo-file-input"
        />
        <input
          ref={camRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
          data-testid="wizard-photo-camera-input"
        />
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-gallery">
          {value.map((p, i) => (
            <div key={i} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-white">
              <img src={p.dataUrl || p.url} alt={p.filename} className="w-full h-28 object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center hover:bg-white"
                aria-label="Foto entfernen"
                data-testid={`photo-remove-${i}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
